import { createMutation, createQuery, useIsMutating } from '@tanstack/svelte-query'
import { Object3D } from 'three'
import { getContext } from 'svelte'
import { get, type Writable } from 'svelte/store'
import { getProject, updateProject, type GcodeGenerationResult } from '../api/projects'
import queryClient from '../constants/query-client'
import { storeScene } from '../stores/scene'
import { downloadBlob } from '../helpers/files'
import { objectToFormData } from '../helpers/form-data'
import type { AxiosResponse } from 'axios'
import ScenePersistence from '../services/scene-persistence'
import { hasUnsavedChanges } from '../stores/unsaved-changes'
import { getPrinterId } from '../helpers/printer'
import { PrinterService } from '../services'
import { ThreeItemKeys } from '../constants/three'
import { DateTime } from 'luxon'
import { kebabCase } from 'es-toolkit'

let sceneSaveWorker: Worker | null = null

function getSceneSaveWorker(): Worker {
  if (!sceneSaveWorker) {
    sceneSaveWorker = new Worker(
      new URL('../workers/scene-save.worker.ts', import.meta.url),
      { type: 'module' },
    )
  }
  return sceneSaveWorker
}

function compressSceneJson(sceneJson: object): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const worker = getSceneSaveWorker()
    worker.onmessage = (e: MessageEvent<ArrayBuffer>) => resolve(e.data)
    worker.onerror = reject
    worker.postMessage(sceneJson)
  })
}

export const useProjectQueries = () => {
  const projectId = get(getContext<Writable<Project['id']>>('projectId'))
  const queryKey = ['project', projectId]

  const useProjectQuery = () => createQuery(() => ({
    queryKey,
    queryFn: () => getProject(projectId),
  }), () => queryClient)

  const updateProjectThreeJsonMutation = createMutation(() => ({
    mutationKey: ['project', projectId, 'data'],
    mutationFn: (data: FormData) => updateProject(projectId, data),
    onError: console.error,
  }), () => queryClient)

  const saveMutationKey = ['project', projectId, 'save']

  const useProjectSaveIsMutating = (): { current: number } => useIsMutating({
    mutationKey: saveMutationKey,
  }, queryClient)

  const saveProjectMutation = createMutation(() => ({
    mutationKey: saveMutationKey,
    mutationFn: async () => {
      const scene = get(storeScene)
      if (!scene) {
        throw new Error('Scene not found')
      }

      const sceneJson = ScenePersistence.prepareForSave(scene)
      const compressedThreeJson = await compressSceneJson(sceneJson)

      const result = await updateProjectThreeJsonMutation.mutateAsync(
        objectToFormData({ 'project[compressedThreeJson]': compressedThreeJson }),
      )

      // The preview thumbnail is only used for the project list card, so it's uploaded
      // in the background instead of blocking the save (canvas.toBlob can be slow).
      const canvas = document.querySelector('.canvas canvas') as HTMLCanvasElement | null
      canvas?.toBlob((previewBlob) => {
        if (!previewBlob) {
          return
        }
        updateProjectThreeJsonMutation.mutate(
          objectToFormData({
            'project[previewImage]': new File([previewBlob], 'preview.jpeg', { type: 'image/jpeg' }),
          }),
        )
      }, 'image/jpeg')

      return result
    },
    onSuccess: (data) => {
      hasUnsavedChanges.set(false)
      const previousProjectData = queryClient.getQueryData<AxiosResponse<Project>>(['project', projectId])
      if (previousProjectData) {
        queryClient.setQueryData(['project', projectId], {
          ...previousProjectData,
          data: {
            ...previousProjectData.data,
            updatedAt: data.data.updatedAt,
          },
        })
      }
    },
  }), () => queryClient)

  const updateProjectNameMutation = createMutation(() => ({
    mutationKey: ['project', projectId, 'name'],
    mutationFn: (name: string) => updateProject(projectId, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project'] })
    },
  }), () => queryClient)

  const loadSceneFromProjectId = async (projectId: string) => {
    const { data: project } = await getProject(projectId)
    const scene = await ScenePersistence.onLoad(project.compressedThreeJsonUrl)
    return scene
  }

  // Reuses the gcode already fetched (and polled to completion) by the preview,
  // read from the shared query cache — the download never triggers a new generation.
  // The button is only enabled once that cache entry is ready (see Header.svelte).
  const downloadPrinterGcodeMutation = createMutation(() => ({
    mutationFn: async (printerId: string) => {
      const gcodeResult = queryClient.getQueryData<GcodeGenerationResult>(
        ['project', projectId, 'gcode', printerId],
      )

      if (gcodeResult?.status !== 'ready') {
        throw new Error('Cannot download gcode: it has not finished generating yet.')
      }

      const scene = await loadSceneFromProjectId(projectId)

      let printer: Object3D | undefined
      scene?.traverse((item) => {
        if (getPrinterId(item) === printerId) {
          printer = item
        }
      })

      const gcodeBlob = new Blob([gcodeResult.gcode], { type: 'application/octet-stream' })

      if (printer) {
        const driver = PrinterService.getPrinterDriver(printer.userData[ThreeItemKeys.PRINTER_MODEL])
        downloadBlob(gcodeBlob, `${kebabCase(printer.name)}-${DateTime.now().toFormat('yyyy-MM-dd-HH-mm')}.${driver.gcodeExtension}`)
      }
      else {
        downloadBlob(gcodeBlob, `gcode-${DateTime.now().toFormat('yyyy-MM-dd-HH-mm')}.mxp`)
      }
    },
  }), () => queryClient)

  return {
    useProjectQuery,
    updateProjectNameMutation,
    updateProjectThreeJsonMutation,
    saveProjectMutation,
    useProjectSaveIsMutating,
    downloadPrinterGcodeMutation,
  }
}
